import { injectable } from 'inversify';
import { plainToClass } from 'class-transformer';

import { HttpService } from '@vk-hr-tek/core/http';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { FilterService, FilterType, Filter } from '@vk-hr-tek/core/filter';

import {
  CreateEventBatchDocumentZipResponse,
  EventXlsxResponse,
  EventPdfResponse,
  CreateEventBatchDocumentZipRequest,
} from '@app/gen/events';

import { GetEventsPdfDto, StartDownloadXlsxDto } from '../../dto';

@injectable()
export class EventsDownloadService {
  constructor(
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private filterService: FilterService,
    private validator: ValidationService,
  ) {}

  async startDownload(
    batchDownloadStartDto: CreateEventBatchDocumentZipRequest,
  ) {
    const { event_ids: eventIds, with_file_attributes: withFileAttrs } =
      batchDownloadStartDto;

    return this.unmarshaller.unmarshall(
      await this.http.post(
        '/event/batch/document/zip',
        {
          event_ids: eventIds,
          with_file_attributes: withFileAttrs,
        },
        { withSide: true },
      ),
      CreateEventBatchDocumentZipResponse,
    );
  }

  async startDownloadXlsx(
    startDownloadXlsx: StartDownloadXlsxDto,
    availableFilters: Filter[],
    byDocuments?: boolean,
  ) {
    const eventsDto = plainToClass(
      this.filterService.buildDto(StartDownloadXlsxDto, availableFilters),
      startDownloadXlsx,
    );

    const filters: FilterType = eventsDto.filters || {};

    return this.unmarshaller.unmarshall(
      await this.http.post(
        `/event/xlsx?type=${byDocuments ? 'documents' : 'events'}`,
        { ...(filters ? { filters } : {}) },
        {
          withSide: true,
        },
      ),
      EventXlsxResponse,
    );
  }

  async startDownloadEventsPdf(
    getEventsPdfDto: GetEventsPdfDto,
    availableFilters: Filter[],
  ) {
    const eventsPdfDto = plainToClass(
      this.filterService.buildDto(GetEventsPdfDto, availableFilters),
      getEventsPdfDto,
    );

    const filters: FilterType = eventsPdfDto.filters || {};

    const result = await this.unmarshaller.unmarshall(
      await this.http.post(
        `/event/pdf`,
        {
          ...(filters
            ? { filters, event_ids: getEventsPdfDto.event_ids }
            : { event_ids: getEventsPdfDto.event_ids }),
        },
        {
          withSide: true,
        },
      ),
      EventPdfResponse,
    );
    return result;
  }
}
